import { SectionHeader } from "./SectionHeader";
import { ConfigParamRow } from "./ConfigParamRow";
import {
  getParameterDisplayName,
  getParameterDescription,
} from "@/utils/parameterNames";

interface GeneralSettingsSectionProps {
  parameters: Record<string, number | [number, number] | boolean>;
  pinnedParameters: Record<string, boolean>;
}

// General Settings parameters in display order (matching LC3 indicator structure)
const GENERAL_RANGE_PARAMETERS = ["neighborscount", "maxbarsback"] as const;
const GENERAL_BOOLEAN_PARAMETERS = ["usedynamicexits"] as const;

/**
 * Determine the ternary state for boolean parameters
 * - If NOT pinned -> "optimize" (will test both true and false)
 * - If pinned AND value is true -> "on"
 * - If pinned AND value is false -> "off"
 *
 * Handles multiple value representations:
 * - boolean: true/false directly
 * - number: 1 = true, 0 = false (TradingView/LC3 convention)
 * - array: [n, n] where n is 0 or 1
 */
const getBooleanState = (
  key: string,
  parameters: Record<string, number | [number, number] | boolean>,
  pinnedParameters: Record<string, boolean>
): "off" | "optimize" | "on" => {
  const isPinned = pinnedParameters[key] ?? false;
  if (!isPinned) return "optimize";

  const value = parameters[key];

  // Handle actual boolean type
  if (typeof value === "boolean") {
    return value ? "on" : "off";
  }

  // Handle array (range stored as tuple)
  if (Array.isArray(value)) {
    return value[0] === 1 ? "on" : "off";
  }

  // Handle number (1 = true, 0 = false per TradingView/LC3 convention)
  return value === 1 ? "on" : "off";
};

/**
 * GeneralSettingsSection - Display General Settings parameters in the study config summary
 *
 * Displays:
 * - neighborscount: range or pinned value
 * - maxbarsback: range or pinned value
 * - usedynamicexits: ternary state (OFF/Optimize/ON)
 */
export function GeneralSettingsSection({
  parameters,
  pinnedParameters,
}: GeneralSettingsSectionProps) {
  return (
    <div>
      <SectionHeader title="General Settings" gradient="purple-pink" />
      <div className="space-y-2">
        {/* Range parameters: neighborscount, maxbarsback */}
        {GENERAL_RANGE_PARAMETERS.map((key) => {
          const value = parameters[key];
          if (value === undefined) return null;

          const isPinned = pinnedParameters[key] === true;
          const displayName = getParameterDisplayName(key);
          const description = getParameterDescription(key);

          // Determine if we should show as range or single number
          const isRange = Array.isArray(value) && value[0] !== value[1];

          return (
            <ConfigParamRow
              key={key}
              label={displayName}
              description={description}
              value={value}
              isPinned={isPinned}
              isOptimizing={!isPinned && isRange}
              type="range"
            />
          );
        })}

        {/* Boolean parameters: usedynamicexits */}
        {GENERAL_BOOLEAN_PARAMETERS.map((key) => {
          const value = parameters[key];
          if (value === undefined) return null;

          const displayName = getParameterDisplayName(key);
          const description = getParameterDescription(key);
          const boolState = getBooleanState(key, parameters, pinnedParameters);

          // Convert ternary state to ConfigParamRow props
          const isOptimizing = boolState === "optimize";
          const boolValue = boolState === "on";

          return (
            <ConfigParamRow
              key={key}
              label={displayName}
              description={description}
              value={boolValue}
              isPinned={!isOptimizing}
              isOptimizing={isOptimizing}
              type="boolean"
            />
          );
        })}
      </div>
    </div>
  );
}
